import React from "react";
import { Grid, TextField, InputAdornment } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

interface CallDurationInputProps {
    value: { hh: string; mm: string; ss: string };
    onChange: (value: { hh: string; mm: string; ss: string }) => void;
    error?:
    | string
    | {
        hh?: string;
        mm?: string;
        ss?: string;
    };
    touched?:
    | boolean
    | {
        hh?: boolean;
        mm?: boolean;
        ss?: boolean;
    };
    setFieldTouched?: (field: string, isTouched: boolean) => void;
}

const isFieldError = (
    err: string | { hh?: string; mm?: string; ss?: string } | undefined
): err is { hh?: string; mm?: string; ss?: string } =>
    typeof err === "object" && err !== null;

const isFieldTouched = (
    tch: boolean | { hh?: boolean; mm?: boolean; ss?: boolean } | undefined
): tch is { hh?: boolean; mm?: boolean; ss?: boolean } =>
    typeof tch === "object" && tch !== null;

const CallDurationInput: React.FC<CallDurationInputProps> = ({
    value,
    onChange,
    error,
    touched,
    setFieldTouched
}) => {
    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        key: "hh" | "mm" | "ss"
    ) => {
        const val = e.target.value.replace(/\D/g, "");
        onChange({ ...value, [key]: val.slice(0, 2) });
    };

    const renderGlobalError = () => {
        if (typeof error === "string" ) {
            return error;
        }

        if (isFieldError(error)) {
            const fieldErrors: string[] = [];

            if (error.hh) fieldErrors.push(error.hh);
            if (error.mm) fieldErrors.push(error.mm);
            if (error.ss) fieldErrors.push(error.ss);

            return fieldErrors.length > 0 ? fieldErrors.join(", ") : null;
        }

        return null;
    };

    return (
        <div className="call-duration-input-container">
            <Grid container spacing={3} alignItems="center">
                <Grid item xs={4} className="call-duration-input">
                    <TextField
                        placeholder="hh"
                        value={value?.hh || ""}
                        onChange={(e) => handleChange(e, "hh")}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <AccessTimeIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            classes: {
                                input: "no-height-reset"
                            }
                        }}
                        variant="standard"
                    />
                </Grid>
                <Grid item xs={4} className="call-duration-input">
                    <TextField
                        placeholder="mm"
                        value={value?.mm || ""}
                        onChange={(e) => handleChange(e, "mm")}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <AccessTimeIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            classes: {
                                input: "no-height-reset"
                            }
                        }}
                        variant="standard"
                    />
                </Grid>
                <Grid item xs={4} className="call-duration-input">
                    <TextField
                        placeholder="ss"
                        value={value?.ss || ""}
                        onChange={(e) => handleChange(e, "ss")}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <AccessTimeIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            classes: {
                                input: "no-height-reset"
                            }
                        }}
                        variant="standard"
                    />
                </Grid>
            </Grid>

            {/* Combined error message below the inputs */}
            {renderGlobalError() && (
                <div
                    className="error-message"
                    style={{ color: "#d32f2f", fontSize: "0.75rem", marginTop: "4px" }}
                >
                    {renderGlobalError()}
                </div>
            )}
        </div>
    );
};

export default CallDurationInput;